import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import _ from 'lodash';

import useProducts from 'src/queries/useProducts';
import { FormValue } from '../useForm';
import { productFormat } from 'src/utils/product';
import { PATH } from 'src/pages/productDetails';
import useQueryUrl from 'src/hooks/useQueryUrl';

import {
  getProductName,
  handleRedirectProductDetail,
  normalizeParenthesis,
} from 'src/utils/helper';
import { Col, Row, Typography, Empty, Tooltip } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { BLANK_OPTION } from 'src/constants/app';
import { SelectField } from 'src/components/form';

type Props = {
  setProduct: (e) => void;
  product: string;
  formInfo: UseFormReturn<FormValue, any>;
  dataReEstimate?: Response.EstimatePriceDetail['item'];
  dataCategoryPage?: SearchParams.CategoryProductSearch;
  prevLang: string;
  lang: string;
  dataProductDimension?: Response.ProductDimension['item'];
  setIsResetMachiningTolerance: (item: boolean) => void;
  setChamferMethod: (item: Response.ChamferItem | null) => void;
  searchKeywordProduct: string;
  setSearchKeywordProduct: (item: string) => void;
  resetInputData: () => void;
  settingShowProductsData?: Response.SettingShowProductsData['item'];
};

const ProductList: React.FC<Props> = ({
  setProduct,
  product,
  formInfo,
  dataReEstimate,
  dataCategoryPage,
  prevLang,
  lang,
  dataProductDimension,
  setIsResetMachiningTolerance,
  setChamferMethod,
  searchKeywordProduct,
  setSearchKeywordProduct,
  resetInputData,
  settingShowProductsData,
}) => {
  const { t } = useTranslation();
  const { control, setValue, clearErrors, watch } = formInfo;

  const varietyCode = watch('variety');
  const shapeCode = watch('shape');

  const { products: listProduct } = useProducts({
    hinshL3Cd: varietyCode,
    keijoL3Cd: shapeCode,
  });
  const [isFilled, setIsFilled] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [isProductLineDisplayConfigured, setIsProductLineDisplayConfigured] =
    useState(false);
  const query = useQueryUrl();

  const productOptions = useMemo(
    () =>
      Array.isArray(listProduct)
        ? listProduct.map((item) => ({
            label: getProductName(item, true),
            value: item.productId,
          }))
        : [],
    [listProduct?.length, JSON.stringify(listProduct), lang],
  );

  // Setting for the number of product lines to display
  useEffect(() => {
    if (
      !isProductLineDisplayConfigured &&
      settingShowProductsData?.imageNumOfLine
    ) {
      if (settingShowProductsData?.imageNumOfLine === 1) {
        setMaxVisibleCount(4);
      } else {
        setMaxVisibleCount(8);
      }
      setIsProductLineDisplayConfigured(true);
    }
  }, [isProductLineDisplayConfigured, settingShowProductsData]);

  useEffect(() => {
    // auto fill value productId when navigate from Category page.
    if (listProduct && dataCategoryPage?.productId && !isFilled) {
      setProduct(dataCategoryPage?.productId);
      setValue('product', dataCategoryPage?.productId);
      setProductItem(dataCategoryPage?.productId);
      setIsFilled(true);
      setSearchKeywordProduct(dataCategoryPage?.productId);
      scrollToActiveProduct(dataCategoryPage?.productId);
      return;
    }
    // auto fill value productId when ReEstimate from OrderHistory page
    if ((dataReEstimate?.hakudoShhnCd || dataReEstimate?.shhnCd) && !isFilled) {
      const productReEstimate = listProduct?.find((item) => {
        // split productId to compare with value from data re-estimate.
        const productSplit = productFormat.splitProductId(item.productId);
        return (
          (dataReEstimate?.hakudoShhnCd &&
            dataReEstimate?.hakudoShhnCd === productSplit.hakudoShhnCd) ||
          (dataReEstimate?.shhnCd &&
            dataReEstimate?.shhnCd === productSplit.shhnCd)
        );
      });
      if (productReEstimate) {
        setProduct(productReEstimate?.productId);
        setValue('product', productReEstimate?.productId);
        setProductItem(productReEstimate?.productId);
        setIsFilled(true);
        setSearchKeywordProduct(productReEstimate?.productId);
        scrollToActiveProduct(productReEstimate?.productId);
      }
      return;
    }
    // auto fill value productId when back from ProductDetail Page
    if (
      (dataProductDimension?.shhnCd ||
        dataProductDimension?.hakudoShhnCd ||
        dataProductDimension?.api1ShhnCd ||
        dataProductDimension?.api2ShhnCd) &&
      !isFilled
    ) {
      const productDetail = listProduct?.find((item) => {
        // split productId to compare with value from data product detail.
        const productSplit = productFormat.splitProductId(item.productId);
        if (dataProductDimension?.shhnCd) {
          return dataProductDimension.shhnCd === productSplit.shhnCd;
        }
        if (dataProductDimension?.hakudoShhnCd) {
          return (
            dataProductDimension.hakudoShhnCd === productSplit.hakudoShhnCd
          );
        }
        if (dataProductDimension?.api1ShhnCd) {
          return dataProductDimension.api1ShhnCd === productSplit.api1ShhnCd;
        }
        if (dataProductDimension?.api2ShhnCd) {
          return dataProductDimension.api2ShhnCd === productSplit.api2ShhnCd;
        }
        return false;
      });
      if (productDetail) {
        setProduct(productDetail?.productId);
        setValue('product', productDetail?.productId);
        setProductItem(productDetail?.productId);
        setIsFilled(true);
        setSearchKeywordProduct(productDetail?.productId);
        scrollToActiveProduct(productDetail?.productId);
      }
      return;
    }
  }, [
    _.get(listProduct, '[0].productId'),
    dataProductDimension,
    dataReEstimate,
    dataCategoryPage?.productId,
  ]);

  const [maxVisibleCount, setMaxVisibleCount] = useState(8);

  const onSelectProduct = (value: string) => {
    setProduct(value);
    setValue('product', value);
    setProductItem(value);
    setIsResetMachiningTolerance(true);
    setChamferMethod(null);
    setValue('chamferItem', {});
    resetInputData();

    const productItem = listProduct?.find(
      (p) => String(p.productId) === String(value),
    );
    if (productItem) {
      setSearchKeywordProduct(productItem?.productId);
      setValue('variety', productItem.hinshL3Cd);
      setValue('shape', productItem.keijoL3Cd);
    } else {
      setSearchKeywordProduct('');
    }
  };

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;
    const handleScroll = () => setOpenId(null);
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const getTooltipContainer = useCallback(
    (triggerNode: HTMLElement) => scrollRef.current || document.body,
    [],
  );

  const setProductItem = (value) => {
    const productItem = listProduct?.find(
      (item) =>
        productFormat.mergeProductId(
          item.hakudoShhnCd,
          item.shhnCd,
          item.api1ShhnCd,
          item.api2ShhnCd,
        ) == value,
    );
    setValue('productItem', productItem);
  };

  const filteredProducts = listProduct || [];

  const visibleProducts = filteredProducts.slice(
    0,
    settingShowProductsData?.imageNumOfLine === 1
      ? maxVisibleCount
      : maxVisibleCount,
  );

  const handleLoadMore = () => {
    setMaxVisibleCount((prev) =>
      settingShowProductsData?.imageNumOfLine === 1 ? prev + 4 : prev + 8,
    );
  };

  const handleBack = () => {
    setMaxVisibleCount(settingShowProductsData?.imageNumOfLine === 1 ? 4 : 8);
  };

  const handleClickTitle = (item) => {
    clearErrors(['variety', 'shape', 'product', 'machiningMethod']);
    onSelectProduct(item.productId);
    setSearchKeywordProduct(item?.productId);
  };

  const productActive = document.querySelector('.product_card.active');
  useEffect(() => {
    if (product && productActive) {
      productActive.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [product, maxVisibleCount, productActive]);

  const _onChangeProduct = (value) => {
    onSelectProduct(value);
    scrollToActiveProduct(value);
  };

  const scrollToActiveProduct = (value) => {
    const selectedIndex = filteredProducts.findIndex(
      (p) => String(p.productId) === String(value),
    );
    if (selectedIndex >= maxVisibleCount) {
      setMaxVisibleCount(selectedIndex + 4);
      setIsProductLineDisplayConfigured(true);
    }
  };

  return (
    <>
      <Row className="conatiner_pricing_productList">
        <Col xs={24} sm={4} md={4} xl={4}>
          <Typography.Text className="form_label_required">
            {t('pricing_page.products_field')}
          </Typography.Text>
        </Col>
        <Col xs={24} sm={19} md={19} lg={19} xl={19}>
          <SelectField
            selectProps={{
              options: [BLANK_OPTION, ...productOptions],
              placeholder: t('pricing_page.products_field'),
              notFoundContent: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t('pricing_page.category_field.no_data')}
                />
              ),
              showSearch: true,
              optionFilterProp: 'label',
              onSelect: (value) => {
                clearErrors(['variety', 'shape', 'product']);
                _onChangeProduct(value);
              },
            }}
            controller={{
              name: 'product',
              control,
            }}
          />
          <Row>
            {/*CJPDJSKY-934: On US environment, do not show this label*/}
            {/*<Typography.Text>{t('pricing_page.description')}</Typography.Text>*/}
          </Row>
        </Col>
      </Row>

      <Row>
        <Col span={24} sm={23} md={23} lg={23} xl={23}>
          <div className="container_list_product">
            <div
              ref={scrollRef}
              className={`product_scroll_area ${
                maxVisibleCount > 16 ? 'scrollable' : ''
              }`}
            >
              {visibleProducts && visibleProducts.length > 0 ? (
                <>
                  {Array.from({
                    length: Math.ceil(visibleProducts.length / 4),
                  }).map((_, rowIndex) => {
                    const rowItems = visibleProducts.slice(
                      rowIndex * 4,
                      rowIndex * 4 + 4,
                    );

                    const rowHasNoImages =
                      rowItems.length > 0 &&
                      rowItems.every(
                        (p) => !(p?.shhnImageDir || p?.hakudoShhnImageDir),
                      );

                    return (
                      <Row
                        key={`row-${rowIndex}`}
                        gutter={[16, 16]}
                        className={rowHasNoImages ? 'no_image_row' : ''}
                      >
                        {rowItems.map((item) => {
                          const productName = getProductName(item, true);
                          const isActive =
                            item?.productId === searchKeywordProduct;

                          const imageSrc =
                            item?.shhnImageDir || item?.hakudoShhnImageDir;

                          const isShowImage =
                            imageSrc &&
                            settingShowProductsData?.imageDisplaySetting;

                          return (
                            <Col
                              key={item.productId}
                              xs={12}
                              sm={12}
                              md={8}
                              lg={6}
                              className="container_product_card"
                            >
                              <div
                                id={product}
                                className={`product_card ${
                                  isActive ? 'active' : ''
                                } ${!isShowImage ? 'no_image' : ''}`}
                                onClick={() => handleClickTitle(item)}
                              >
                                <div
                                  className="product_image-wrap"
                                  style={{ height: isShowImage ? 100 : 0 }}
                                >
                                  {isShowImage && (
                                    <img
                                      src={imageSrc}
                                      alt="product img"
                                      className="product_image"
                                    />
                                  )}
                                </div>

                                <div className="product_content">
                                  <Tooltip
                                    title={normalizeParenthesis(productName)}
                                    placement="top"
                                    overlayInnerStyle={{
                                      backgroundColor: '#fff',
                                      color: '#000',
                                    }}
                                    getPopupContainer={getTooltipContainer}
                                    visible={openId === item?.productId}
                                    onVisibleChange={(nextVisible) =>
                                      setOpenId(
                                        nextVisible ? item?.productId : null,
                                      )
                                    }
                                  >
                                    <Typography.Title
                                      level={5}
                                      className="title_img__product"
                                    >
                                      {normalizeParenthesis(productName)}
                                    </Typography.Title>
                                  </Tooltip>

                                  <Typography.Text className="product_detail_link">
                                    <span
                                      onClick={() =>
                                        handleRedirectProductDetail(
                                          item,
                                          query?.hinshL3Cd,
                                          PATH.replace(
                                            ':origin',
                                            location.pathname,
                                          ),
                                        )
                                      }
                                    >
                                      {t('pricing_page.detail')}
                                    </span>
                                  </Typography.Text>
                                </div>
                              </div>
                            </Col>
                          );
                        })}
                      </Row>
                    );
                  })}
                </>
              ) : (
                <Typography.Text className="empty_data_message">
                  {t('pricing_page.empty_data_message')}
                </Typography.Text>
              )}
            </div>

            {filteredProducts && filteredProducts.length > 4 && (
              <div className="product_navigation_inline">
                {filteredProducts.length > maxVisibleCount && (
                  <span className="nav_text" onClick={handleLoadMore}>
                    {t('pricing_page.more')} <DownOutlined />
                  </span>
                )}
                {((settingShowProductsData?.imageNumOfLine === 1 &&
                  maxVisibleCount > 4) ||
                  (settingShowProductsData?.imageNumOfLine !== 1 &&
                    maxVisibleCount > 8)) && (
                  <span className="nav_text" onClick={handleBack}>
                    <UpOutlined /> {t('pricing_page.less')}
                  </span>
                )}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </>
  );
};

export default ProductList;
